/**
 * Created by Ning Jiang on 3/14/2017.
 */

import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {IReelStoppedData} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {ReelStopPresentationController} from "@nolimitcity/slot-game/bin/core/reelstoppresentation/ReelStopPresentationController";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {Elastic, Power4, TimelineLite, TweenLite, TweenMax} from "gsap";
import {TeslaParsedGameData} from "../../server/data/TeslaParsedGameData";
import {Tesla} from "../../Tesla";
import {TeslaGameConfig} from "../../TeslaGameConfig";
import {TeslaSoundConfig} from "../../TeslaSoundConfig";
import {StateMatrix} from "../../utils/StateMatrix";
import {TeslaBonusHighlightSpinAnimation} from "./TeslaBonusHighlightSpinAnimation";

export interface SymbolData {
    symbol:StateReelSymbol;
    position:PIXI.Point;
}

export class TeslaMainGameBonusController extends ReelStopPresentationController {
    private _mode:string;
    private _bonusSymbolTriggered:boolean;
    private _overlayWilds:boolean[][];
    private _attentionTimeline:TimelineLite | null;
    private _wildTransformAnimation:TimelineLite | null;

    private _winSymbolId:string;
    private _bonusSymbolLoop:TimelineLite | null;
    private _shouldLoop:boolean;
    private _bonusSymbolData:SymbolData;
    private _wildSymbolsData:SymbolData[];
    private _spinData:string[];
    private _symbolMatrix:StateMatrix<string>;

    constructor(index:number) {
        super(index, "TeslaMainGameBonusController");
    }

    protected parseFeatureGameData(data:TeslaParsedGameData):void {
        this._mode = data.mode;
        this._bonusSymbolTriggered = data.bonusSymbolTriggered;
        this._overlayWilds = data.overlayWilds!;

        if(this._bonusSymbolTriggered) {
            this._winSymbolId = data.wildSymbol;
            this._symbolMatrix = new StateMatrix<string>(5, 5, [1, 3], "none");
            this._symbolMatrix.init(data.reels.concat());
            this._symbolMatrix = TeslaBonusHighlightSpinAnimation.removeInvalidSymbolIds(this._symbolMatrix);
            this._spinData = TeslaBonusHighlightSpinAnimation.createFakeSpinData(this._symbolMatrix, this._winSymbolId);
        }
    }

    private shouldPlay(data:IReelStoppedData) {
        if(this._mode !== GameMode.NORMAL) {
            return false;
        }

        if(data.reelId != 4) {
            return false;
        }

        return this._bonusSymbolTriggered;
    }

    public onReelBounce(data:IReelStoppedData):void {
        if(this.shouldPlay(data)) {
            this._bonusSymbolData = this.getBonusSymbolData();
            this.playBonusSymbolAttention(data);
        }
    }

    public onReelSpinStopped(data:IReelStoppedData):void {
        if(this.shouldPlay(data)) {
            this._wildSymbolsData = this.getWildSymbolsData(this._overlayWilds);
            this.playWildTransformAnimation(data);
        } else {
            Tesla.reelFrame.resetGaugeView();
            this.dispatchCompleteEvent(data.reelId);
        }
    }

    private playWildTransformAnimation(data:IReelStoppedData):void {
        this._wildTransformAnimation = new TimelineLite();

        if(data.fastSpin || data.quickStop) {
            this._spinData.splice(0, 8);
        }
        this._wildTransformAnimation.add(TeslaBonusHighlightSpinAnimation.getHighlightSpinAnimation(this._spinData));
        this._wildTransformAnimation.add([
            TeslaBonusHighlightSpinAnimation.getHighlightWinAnimation(this._winSymbolId),
            () => SlotGame.sound.playEffect(TeslaSoundConfig.instance.BONUS_BALL_LIGHTNING)
        ]);
        this._wildTransformAnimation.add(this.getTransformationAnimations());
        this._wildTransformAnimation.add(() => this.stopBonusSymbolLoop());
        this._wildTransformAnimation.add(this.transformBonusSymbolToWild());
        this._wildTransformAnimation.add(() => this.dispatchCompleteEvent(data.reelId));
    }


    private playBonusSymbolAttention(data:IReelStoppedData) {
        this._attentionTimeline = new TimelineLite();
        this._attentionTimeline.add([
            () => this.startBonusSymbolLoop(this._bonusSymbolData.symbol),
            () => SlotGame.sound.playEffect(TeslaSoundConfig.instance.BONUS_BALL_LANDING),
        ]);

        this._attentionTimeline.add(TweenMax.fromTo(this._bonusSymbolData.symbol.scale, 0.5, {x : 1.3, y : 1.3}, {
            x : 1,
            y : 1,
            yoyo : true
        }));

    }

    private startBonusSymbolLoop(symbol:StateReelSymbol) {
        this._shouldLoop = true;
        this.loopAnimation(symbol);
    }

    private stopBonusSymbolLoop() {
        this._shouldLoop = false;
        if(this._bonusSymbolLoop && this._bonusSymbolLoop.isActive()) {
            this._bonusSymbolLoop.pause();
        }
        this._bonusSymbolLoop = null;
    }

    private transformBonusSymbolToWild():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add(new TweenLite(this._bonusSymbolData.symbol.scale, 0.2, {x : 0, y : 0, ease : Power4.easeIn}));
        tl.add(() => {
            this._bonusSymbolData.symbol.replaceWith(ReelSymbolName.WILD, this._bonusSymbolData.symbol.reelId, TeslaGameConfig.instance.SYMBOL_STATES.normal)
        });
        tl.add(new TweenLite(this._bonusSymbolData.symbol.scale, 0.2, {
            x : 1,
            y : 1,
            ease : Elastic.easeOut.config(2.5, 1)
        }));

        return tl;
    }

    private loopAnimation(symbol:StateReelSymbol) {
        this._bonusSymbolLoop = new TimelineLite();
        if(this._shouldLoop) {
            this._bonusSymbolLoop.add(symbol.changeState({
                state : TeslaGameConfig.instance.SYMBOL_STATES.win,
                frames : [7, 39],
                onCompleteCallback : () => {
                    this.loopAnimation(symbol);
                }
            }));
        }
    }

    protected resetFeature():void {
        this._wildTransformAnimation = null;
        Tesla.bonusSymbolView.resetBonusSymbolView();
    }

    private getBonusSymbolData():SymbolData {
        let bonusSymbol:StateReelSymbol | null = null;
        let bonusPosition:PIXI.Point = new PIXI.Point();
        for(let i:number = 0; i < 3; i++) {
            const symbol = Reels.getSymbol(4, i);
            if(symbol && symbol.symName === ReelSymbolName.BONUS) {
                bonusSymbol = symbol!;
                let positions:[number, number] = Helper.getSymbolPositions(4, i);
                bonusPosition.set(positions[0], positions[1]);
                break;
            }
        }

        if(bonusSymbol == null) {
            debugger;
            throw new Error("TeslaMainGameBonusController.tryPlayAttention(): Cannot find B symbol!");
        }
        return {symbol : bonusSymbol, position : bonusPosition};
    }

    private getWildSymbolsData(overlayWilds:boolean[][]):SymbolData[] {
        let wildSymbols:any[] = [];
        for(let i:number = 0; i < overlayWilds.length; i++) {
            for(let j:number = 0; j < overlayWilds[i].length; j++) {
                if(overlayWilds[i][j]) {
                    const symbol = Reels.getSymbol(i, j);
                    const basePosition:number[] = Helper.getSymbolPositions(i, j);
                    const position = new PIXI.Point(basePosition[0], basePosition[1]);
                    wildSymbols.push({symbol : symbol, position : position});
                }
            }
        }
        return wildSymbols;
    }

    private getTransformationAnimations():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        let transformAnimations:TimelineLite[] = Tesla.bonusSymbolView.getTransformAnimations(this._bonusSymbolData.position, this._wildSymbolsData);
        tl.add(transformAnimations, 0, "start", 0.1);
        return tl;
    }


}